import { FaceLoginAttemptReason } from '@reliance/types/enums/FaceLoginAttemptReason';
import { FaceLoginMode } from '@reliance/types/enums/FaceLoginMode';
import { FaceLoginAttempt } from '@reliance/types/models/FaceLoginAttempt';

export const FACE_LOGIN_ATTEMPT_REASON_LABELS: Record<FaceLoginAttemptReason, string> = {
  [FaceLoginAttemptReason.SUCCESS]: 'Success',
  [FaceLoginAttemptReason.SIGNATURE_MISMATCH]: "Didn't match",
  [FaceLoginAttemptReason.LOW_SHARPNESS]: 'Out of focus',
  [FaceLoginAttemptReason.POSE_OUT_OF_RANGE]: 'Face missing/turned',
  [FaceLoginAttemptReason.NO_FACE]: 'No face',
  [FaceLoginAttemptReason.TOO_FAR]: 'Too far',
  [FaceLoginAttemptReason.TOO_CLOSE]: 'Too close',
  [FaceLoginAttemptReason.PROCESSING_ERROR]: 'Processing error',
  [FaceLoginAttemptReason.TIMEOUT]: 'Timed out',
  [FaceLoginAttemptReason.CANCELLED]: 'Cancelled',
  [FaceLoginAttemptReason.INTERRUPTED]: 'Interrupted',
  [FaceLoginAttemptReason.NO_SIGNATURE]: 'No signature',
  [FaceLoginAttemptReason.MODEL_UNAVAILABLE]: 'Model unavailable',
};

export const FACE_LOGIN_MODE_LABELS: Record<FaceLoginMode, string> = {
  [FaceLoginMode.CONTINUOUS]: 'Continuous',
  [FaceLoginMode.SINGLE_SHOT]: 'Single shot',
};

export const FACE_LOGIN_ATTEMPT_REASON_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '(any)' },
  ...Object.values(FaceLoginAttemptReason).map((reason) => ({
    value: reason,
    label: FACE_LOGIN_ATTEMPT_REASON_LABELS[reason],
  })),
];

export const FACE_LOGIN_PASSED_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '(any)' },
  { value: 'true', label: 'Passed' },
  { value: 'false', label: 'Failed' },
];

export function reasonLabel(reason: FaceLoginAttemptReason | string): string {
  if (Object.prototype.hasOwnProperty.call(FACE_LOGIN_ATTEMPT_REASON_LABELS, reason)) {
    return FACE_LOGIN_ATTEMPT_REASON_LABELS[reason as FaceLoginAttemptReason];
  }
  return String(reason);
}

export function attemptTimestamp(attempt: FaceLoginAttempt) {
  return attempt.dateAttempted ?? attempt.dateReceived;
}

export function sharpnessWarning(attempt: FaceLoginAttempt): string | null {
  if (attempt.bestSharpnessBelowThreshold === true) {
    return 'Out of focus';
  }
  return null;
}

export function scoreWarning(attempt: FaceLoginAttempt): string | null {
  if (
    attempt.bestScore != null &&
    attempt.effectiveCosineThreshold != null &&
    attempt.bestScore < attempt.effectiveCosineThreshold
  ) {
    return 'Below threshold';
  }
  return null;
}

export function facesWarning(attempt: FaceLoginAttempt): string | null {
  if (attempt.maxFaceCount === 0) {
    return 'No face';
  }
  return null;
}

export function brightnessWarning(attempt: FaceLoginAttempt): string | null {
  if (attempt.bestBrightness == null) {
    return null;
  }
  if (attempt.bestBrightness < 25) {
    return 'Too dark';
  }
  if (attempt.bestBrightness > 90) {
    return 'Too bright';
  }
  return null;
}

export function faceAreaWarning(attempt: FaceLoginAttempt): string | null {
  if (attempt.reason === FaceLoginAttemptReason.TOO_FAR) {
    return 'Too far';
  }
  if (attempt.reason === FaceLoginAttemptReason.TOO_CLOSE) {
    return 'Too close';
  }
  return null;
}

export function poseWarning(attempt: FaceLoginAttempt): string | null {
  if (attempt.reason === FaceLoginAttemptReason.POSE_OUT_OF_RANGE) {
    return 'Out of range';
  }
  return null;
}

/**
 * Maps sessionId to a stable index for alternating row styles
 * that we can group by the sessionId so they have the same background color.
 */
export function sessionStyleIndexById(attempts: FaceLoginAttempt[]): Map<string, number> {
  const indexes = new Map<string, number>();
  let nextIndex = 0;
  for (const attempt of attempts) {
    if (!indexes.has(attempt.sessionId)) {
      indexes.set(attempt.sessionId, nextIndex);
      nextIndex += 1;
    }
  }
  return indexes;
}
